import { useEffect, useMemo, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Phone,
    PhoneIncoming,
    PhoneOff,
    Minus,
    UserPlus,
    Search,
    Briefcase,
    X,
    Clock,
    ArrowLeft,
    CheckCircle2,
    Loader2,
    StickyNote,
    AlertTriangle,
    Sparkles,
    Mic,
    MicOff,
    Grid3x3,
    Pause,
    Play,
    PhoneForwarded,
    GraduationCap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
    useIncomingCall,
    openOutboundCall,
    CALL_DOCK_ENABLED,
    type IncomingCall,
    type CallAiDraft,
    type TranscriptLine,
} from "@/lib/use-incoming-call";
import { useSoftphone } from "@/lib/use-softphone";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { QuickBookForm } from "@/components/quick-book-panel";
import { isContactStale, formatVerifiedLabel } from "@/lib/contactFreshness";
import { toast } from "sonner";

interface SearchHit {
    type: string;
    id: string;
    title: string;
    subtitle?: string;
}

interface CustomerHit {
    id: string;
    company_name: string;
    subtitle?: string;
}

interface CustomerSummary {
    id: string;
    company_name: string;
    phone: string;
    email: string;
    open_jobs: number;
    last_job_title: string | null;
    last_job_date: string | null;
    location_count: number;
    asset_count: number;
    devices_due_90d: number;
    last_test_at: string | null;
    next_scheduled_visit: string | null;
    site_contact_last_verified_at: string | null;
}

interface IntakeConflict {
    field: string;
    conflict_customer_id: string;
    conflict_company_name: string;
    detail: string | null;
}

/** Local sub-step the panel is showing on top of the call lifecycle state. */
type SubView = "none" | "newCustomer" | "job" | "created";

/**
 * Call Panel (evolved from CallDock): a non-modal slide-in rendered at the
 * app-shell layer. Two entry points share the same UI:
 *   - Telephony: an inbound call arrives on the CallBus (status "ringing").
 *   - Walk-up: the office opens it manually (top bar / Cmd+J) — a synthetic
 *     session with status "search", origin "walkup".
 * Every action stays in-panel: search, view summary, create customer+site
 * atomically, and book a job via the embedded QuickBookForm. No navigation.
 */
export function CallDock() {
    const navigate = useNavigate();
    const { call, accept, decline, end, startWrapup, updateNotes, setCreatedJobId, setMatchedCustomer } =
        useIncomingCall();
    const softphone = useSoftphone();

    // When a real Twilio call is present (softphone active), the panel buttons
    // drive the actual call audio in addition to the panel lifecycle. Without a
    // live call (mock / observer mode), these fall back to lifecycle-only.
    const handleAccept = () => {
        if (softphone.hasCall) softphone.answer();
        accept();
    };
    const handleDecline = () => {
        if (softphone.hasCall) softphone.reject();
        decline();
    };
    const handleEndCall = () => {
        if (softphone.hasCall) softphone.hangup();
        startWrapup();
    };
    // Click-to-dial: place an outbound call from the office softphone and show
    // the callee in the panel with an active-call timer.
    const handleCall = (phone: string, customerId?: string, name?: string) => {
        if (!phone) return;
        openOutboundCall({ toNumber: phone, matchedCustomerId: customerId, fromName: name });
        void softphone.dial(phone);
    };
    const [minimized, setMinimized] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [subView, setSubView] = useState<SubView>("none");
    const [lastJobId, setLastJobId] = useState<string | null>(null);

    const isWalkup = call?.origin === "walkup";

    useEffect(() => {
        if (call?.status === "ringing") setMinimized(false);
    }, [call?.id, call?.status]);

    // Reset the local sub-step whenever a new session starts so a fresh call /
    // walk-up never inherits the previous one's wizard position.
    useEffect(() => {
        setSubView("none");
        setSearchTerm("");
        setLastJobId(null);
    }, [call?.id]);

    // Auto-dismiss wrapup after 30s
    const wrapupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (call?.status === "wrapup") {
            wrapupTimerRef.current = setTimeout(() => end(), 30_000);
        }
        return () => {
            if (wrapupTimerRef.current) clearTimeout(wrapupTimerRef.current);
        };
    }, [call?.status, end]);

    const formattedNumber = useMemo(() => formatPhoneNumber(call?.fromNumber ?? ""), [call?.fromNumber]);

    const queryNeedle = searchTerm || (call?.fromNumber ? digitsOnly(call.fromNumber).slice(-7) : "");
    const { data: searchData } = useQuery({
        queryKey: ["call-panel", "search", queryNeedle],
        queryFn: () => api.get<{ results: SearchHit[] }>(`/api/search?q=${encodeURIComponent(queryNeedle)}`),
        enabled: !!call && queryNeedle.length >= 2,
    });
    const matches: CustomerHit[] = useMemo(
        () =>
            (searchData?.results ?? [])
                .filter((r) => r.type === "customer")
                .map((r) => ({ id: r.id, company_name: r.title, subtitle: r.subtitle })),
        [searchData],
    );

    const { data: customerSummary } = useQuery({
        queryKey: ["call-panel", "customer-summary", call?.matchedCustomerId],
        queryFn: () => api.get<CustomerSummary>(`/api/customers/${call!.matchedCustomerId}/summary`),
        enabled: !!call?.matchedCustomerId,
    });

    if (!call) return null;

    const isSearch = call.status === "search";
    const isRinging = call.status === "ringing";
    const isActive = call.status === "active";
    const isWrapup = call.status === "wrapup";

    // Minimized pill only applies to telephony — a walk-up session has nothing
    // to "ring in the background", so it closes instead of minimizing.
    if (minimized && !isWalkup) {
        return (
            <button
                type="button"
                onClick={() => setMinimized(false)}
                aria-label="Expand call panel"
                aria-live="polite"
                className={cn(
                    "fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg transition-all",
                    isRinging
                        ? "bg-primary text-primary-foreground animate-pulse hover:bg-primary/90"
                        : isWrapup
                          ? "bg-amber-700 text-white hover:bg-amber-800"
                          : "bg-emerald-700 text-white hover:bg-emerald-800",
                )}
            >
                {isRinging ? <PhoneIncoming className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                <span className="text-xs font-semibold">{formattedNumber || "Call"}</span>
            </button>
        );
    }

    const goToJob = () => setSubView("job");
    const goToNewCustomer = () => setSubView("newCustomer");

    // One-tap manual call log for staff handling calls on the main business
    // number — no wrap-up flow, no active-call state required.
    const handleQuickLog = async (customerId: string, name?: string) => {
        try {
            await api.post("/api/call-logs", { customerId, direction: "inbound", source: "manual" });
            toast.success(name ? `Call logged for ${name}` : "Call logged");
        } catch {
            toast.error("Failed to log call");
        }
    };

    // Start a practice call against the AI Training Simulator. The server rings
    // this softphone with the simulated customer; the softphone auto-answers and
    // the panel switches to the active training call.
    const handleStartTraining = async () => {
        const ok = await softphone.startTraining();
        if (ok) {
            toast.info("Training call incoming…", { description: "Answering automatically — talk to the customer." });
        } else {
            toast.error("Couldn't start the training call.", {
                description: "Check training is configured and you're set as the tester (ring-only / conference).",
            });
        }
    };

    return (
        // Non-modal: no full-screen backdrop, so staff can keep interacting with
        // the page behind the panel while it is open. The panel is its own
        // z-layer column anchored to the right edge.
        <div
            role="dialog"
            aria-label="Call Panel"
            aria-live="polite"
            className="fixed right-0 top-0 bottom-0 z-50 w-[24rem] max-w-full border-l bg-card text-card-foreground shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        >
            <PanelHeader
                call={call}
                formattedNumber={formattedNumber}
                allowMinimize={!isWalkup}
                onMinimize={() => setMinimized(true)}
                onClose={end}
            />

            {call.training && (
                <div className="flex shrink-0 items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-[11px] font-semibold tracking-wide text-amber-700 dark:text-amber-400">
                    <span className="rounded bg-amber-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-white">
                        Training
                    </span>
                    Simulated customer — not a real caller
                </div>
            )}

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* ── Sub-step: New customer intake ───────────────────────── */}
                {subView === "newCustomer" && (
                    <NewCustomerIntake
                        defaultPhone={call.fromNumber}
                        defaultName={call.fromName}
                        draft={call.aiDraft}
                        onBack={() => setSubView("none")}
                        onReady={(customerId) => {
                            setMatchedCustomer(customerId);
                            setSubView("job");
                        }}
                    />
                )}

                {/* ── Sub-step: Job creation (embedded QuickBookForm) ─────── */}
                {subView === "job" && (
                    <div className="space-y-3">
                        <button
                            type="button"
                            onClick={() => setSubView("none")}
                            className="flex items-center gap-1 text-xs text-foreground/80 hover:text-foreground transition-colors"
                        >
                            <ArrowLeft className="h-3 w-3" />
                            Back
                        </button>
                        <QuickBookForm
                            initialCustomerId={call.matchedCustomerId}
                            initialCustomerName={customerSummary?.company_name}
                            onCreated={(jobId) => {
                                setLastJobId(jobId);
                                setCreatedJobId(jobId);
                                setSubView("created");
                            }}
                            onDone={() => setSubView("none")}
                        />
                    </div>
                )}

                {/* ── Sub-step: Created confirmation ──────────────────────── */}
                {subView === "created" && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
                            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
                            <div className="text-sm font-semibold">Job created</div>
                            <div className="text-xs text-foreground/80">It is now in the dispatch backlog.</div>
                        </div>
                        <div className="grid gap-2">
                            {/* G5: same-tab — the dock floats above every page, so
                                navigating keeps the call context visible. */}
                            {lastJobId && (
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => navigate(`/jobs/${lastJobId}`)}
                                >
                                    Open job
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                className="w-full"
                                onClick={() => {
                                    setSubView("none");
                                    setSearchTerm("");
                                }}
                            >
                                Handle another
                            </Button>
                            <Button className="w-full" onClick={end}>
                                Done
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── Lifecycle states (only when no sub-step is active) ──── */}
                {subView === "none" && (
                    <>
                        {/* Search / walk-up entry */}
                        {isSearch && (
                            <>
                                {customerSummary && (
                                    <CustomerQuickInfo
                                        summary={customerSummary}
                                        onLogCall={() =>
                                            handleQuickLog(customerSummary.id, customerSummary.company_name)
                                        }
                                        onCall={
                                            softphone.ready && customerSummary.phone
                                                ? () =>
                                                      handleCall(
                                                          customerSummary.phone,
                                                          customerSummary.id,
                                                          customerSummary.company_name,
                                                      )
                                                : undefined
                                        }
                                    />
                                )}
                                {call.matchedCustomerId && (
                                    <Button className="w-full" onClick={goToJob}>
                                        <Briefcase className="mr-2 h-4 w-4" />
                                        Create Job
                                    </Button>
                                )}
                                <CustomerSearch
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    matches={matches}
                                    onSelectCustomer={(id) => setMatchedCustomer(id)}
                                    onLogCall={handleQuickLog}
                                />
                                <Button variant="outline" className="w-full" onClick={goToNewCustomer}>
                                    <UserPlus className="mr-2 h-4 w-4" />
                                    New Customer
                                </Button>
                                {softphone.trainingEnabled && (
                                    <button
                                        type="button"
                                        onClick={handleStartTraining}
                                        className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 px-3 py-2 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-500/10 dark:text-amber-400"
                                    >
                                        <GraduationCap className="h-4 w-4" />
                                        Start training call
                                    </button>
                                )}
                            </>
                        )}

                        {/* Ringing */}
                        {isRinging && (
                            <>
                                {customerSummary && <CustomerQuickInfo summary={customerSummary} />}
                                <RingingActions onAccept={handleAccept} onDecline={handleDecline} />
                                <CustomerSearch
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    matches={matches}
                                    onSelectCustomer={(id) => setMatchedCustomer(id)}
                                />
                            </>
                        )}

                        {/* Active call */}
                        {isActive && (
                            <>
                                {customerSummary && <CustomerQuickInfo summary={customerSummary} />}
                                {(call.transcript?.length ?? 0) > 0 && (
                                    <LiveTranscript lines={call.transcript ?? []} live={softphone.hasCall} />
                                )}
                                {hasDraftContent(call.aiDraft) && call.aiDraft && (
                                    <AiDraftCard draft={call.aiDraft} onUse={goToNewCustomer} />
                                )}
                                {softphone.hasCall && (
                                    <CallControls
                                        muted={softphone.muted}
                                        onToggleMute={() => softphone.toggleMute()}
                                        onSendDigit={(d) => softphone.sendDigits(d)}
                                        conference={softphone.conferenceMode && !!call.callSid}
                                        callSid={call.callSid}
                                    />
                                )}
                                <div>
                                    <label className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
                                        Call Notes
                                    </label>
                                    <Textarea
                                        value={call.notes ?? ""}
                                        onChange={(e) => updateNotes(e.target.value)}
                                        placeholder="Capture anything the copilot missed..."
                                        className="mt-1 min-h-[56px] text-sm"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <PanelShortcut icon={Briefcase} label="Create Job" primary onClick={goToJob} />
                                    <PanelShortcut icon={UserPlus} label="Add Customer" onClick={goToNewCustomer} />
                                </div>
                                <CustomerSearch
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    matches={matches}
                                    onSelectCustomer={(id) => setMatchedCustomer(id)}
                                />
                                <Button variant="destructive" className="w-full" onClick={handleEndCall}>
                                    <PhoneOff className="mr-2 h-4 w-4" />
                                    End Call
                                </Button>
                            </>
                        )}

                        {/* Wrap-up */}
                        {isWrapup && (
                            <WrapUpView
                                call={call}
                                customerSummary={customerSummary}
                                onSaveNotes={async () => {
                                    try {
                                        const durationSeconds =
                                            call.callStartedAt && call.callEndedAt
                                                ? Math.round((call.callEndedAt - call.callStartedAt) / 1000)
                                                : undefined;
                                        const noteParts = [
                                            call.fromName ? `Caller: ${call.fromName}` : null,
                                            call.notes || null,
                                        ].filter(Boolean);
                                        await api.post("/api/call-logs", {
                                            callerNumber: call.fromNumber || undefined,
                                            direction: "inbound",
                                            notes: noteParts.join("\n") || undefined,
                                            ...(call.matchedCustomerId ? { customerId: call.matchedCustomerId } : {}),
                                            ...(durationSeconds !== undefined ? { durationSeconds } : {}),
                                        });
                                        toast.success("Call log saved");
                                    } catch {
                                        toast.error("Failed to save call log");
                                    }
                                }}
                                onClose={end}
                            />
                        )}
                    </>
                )}

                {CALL_DOCK_ENABLED && subView === "none" && (
                    <div className="text-[10px] text-foreground/80 border-t pt-2">
                        Dev: trigger a call with{" "}
                        <code className="font-mono bg-muted px-1 rounded">triggerMockCall()</code>.
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Sub-Components ──────────────────────────────────────────────── */

function PanelHeader({
    call,
    formattedNumber,
    allowMinimize,
    onMinimize,
    onClose,
}: {
    call: IncomingCall;
    formattedNumber: string;
    allowMinimize: boolean;
    onMinimize: () => void;
    onClose: () => void;
}) {
    const statusLabels: Record<string, string> = {
        search: "Call Panel",
        ringing: "Incoming Call",
        active: "On Call",
        intake: "Command Intake — New Job",
        wrapup: "Call Wrap-Up",
        ended: "Call Ended",
    };
    const isRinging = call.status === "ringing";
    const isWrapup = call.status === "wrapup";
    const isSearch = call.status === "search";

    return (
        <div
            className={cn(
                "flex items-center justify-between border-b px-4 py-3 shrink-0",
                isRinging
                    ? "bg-primary text-primary-foreground animate-pulse"
                    : isSearch
                      ? "bg-primary text-primary-foreground"
                      : isWrapup
                        ? "bg-gradient-to-r from-amber-700 to-amber-600 text-white"
                        : "bg-gradient-to-r from-emerald-700 to-emerald-600 text-white",
            )}
        >
            <div className="flex items-center gap-2 min-w-0">
                {isRinging ? <PhoneIncoming className="h-4 w-4 shrink-0" /> : <Phone className="h-4 w-4 shrink-0" />}
                <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{statusLabels[call.status] ?? "Call"}</div>
                    {(formattedNumber || call.fromName) && (
                        <div className="text-xs opacity-90 truncate">
                            {formattedNumber}
                            {call.fromName ? ` · ${call.fromName}` : ""}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2">
                {call.status === "active" && <HeaderLive startedAt={call.callStartedAt} />}
                <div className="flex items-center gap-1">
                    {allowMinimize && (
                        <button
                            type="button"
                            onClick={onMinimize}
                            aria-label="Minimize"
                            className="rounded p-1 hover:bg-white/15 transition-colors"
                        >
                            <Minus className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="rounded p-1 hover:bg-white/15 transition-colors"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function CustomerQuickInfo({
    summary,
    onLogCall,
    onCall,
}: {
    summary: CustomerSummary;
    onLogCall?: () => void;
    onCall?: () => void;
}) {
    const stale = isContactStale(summary.site_contact_last_verified_at);
    return (
        <div className="space-y-2 rounded-xl border bg-gradient-to-b from-card to-secondary/25 p-3 shadow-sm">
            <div className="text-sm font-bold">{summary.company_name}</div>
            <div className="grid grid-cols-2 gap-1.5 text-xs text-foreground/80">
                <div>
                    Open Jobs: <span className="font-bold text-foreground">{summary.open_jobs}</span>
                </div>
                <div>
                    Due ≤90d: <span className="font-bold text-foreground">{summary.devices_due_90d}</span>
                </div>
                <div>
                    Locations: <span className="font-bold text-foreground">{summary.location_count}</span>
                </div>
                <div>
                    Assets: <span className="font-bold text-foreground">{summary.asset_count}</span>
                </div>
                <div className="col-span-2">
                    Last test:{" "}
                    <span className="font-medium text-foreground">{formatShortDate(summary.last_test_at)}</span>
                </div>
                <div className="col-span-2">
                    Next visit:{" "}
                    <span className="font-medium text-foreground">{formatShortDate(summary.next_scheduled_visit)}</span>
                </div>
                {summary.last_job_title && (
                    <div className="col-span-2 truncate">
                        Last: <span className="font-medium text-foreground">{summary.last_job_title}</span>
                    </div>
                )}
            </div>
            {stale && (
                <div className="flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>Contact stale — {formatVerifiedLabel(summary.site_contact_last_verified_at)}</span>
                </div>
            )}
            {onCall && (
                <Button size="sm" className="w-full" onClick={onCall}>
                    <Phone className="mr-2 h-3.5 w-3.5" />
                    Call{summary.phone ? ` ${formatPhoneNumber(summary.phone)}` : ""}
                </Button>
            )}
            {onLogCall && (
                <Button variant="outline" size="sm" className="w-full" onClick={onLogCall}>
                    <Phone className="mr-2 h-3.5 w-3.5" />
                    Log Call
                </Button>
            )}
        </div>
    );
}

/**
 * Live indicator shown in the header during an active call: a small animated
 * equalizer (call is live) plus the running mm:ss timer. White-on-color so it
 * reads on the emerald active-call header.
 */
function HeaderLive({ startedAt }: { startedAt?: number }) {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!startedAt) return;
        setElapsed(Math.floor((Date.now() - startedAt) / 1000));
        const interval = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);
        return () => clearInterval(interval);
    }, [startedAt]);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return (
        <div className="flex items-center gap-1.5">
            <span className="flex h-3.5 items-end gap-[2px]" aria-hidden>
                <span
                    className="w-[3px] rounded-full bg-white/90 animate-pulse"
                    style={{ height: 6, animationDelay: "0ms" }}
                />
                <span
                    className="w-[3px] rounded-full bg-white/90 animate-pulse"
                    style={{ height: 12, animationDelay: "150ms" }}
                />
                <span
                    className="w-[3px] rounded-full bg-white/90 animate-pulse"
                    style={{ height: 8, animationDelay: "300ms" }}
                />
                <span
                    className="w-[3px] rounded-full bg-white/90 animate-pulse"
                    style={{ height: 14, animationDelay: "450ms" }}
                />
            </span>
            {startedAt && (
                <span className="rounded bg-black/15 px-1.5 py-0.5 font-mono text-xs font-semibold tabular-nums">
                    {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
                </span>
            )}
        </div>
    );
}

function CustomerSearch({
    searchTerm,
    setSearchTerm,
    matches,
    onSelectCustomer,
    onLogCall,
}: {
    searchTerm: string;
    setSearchTerm: (v: string) => void;
    matches?: CustomerHit[];
    onSelectCustomer: (id: string) => void;
    onLogCall?: (id: string, name?: string) => void;
}) {
    return (
        <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
                Match Customer
            </label>
            <div className="relative mt-1">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-foreground/80" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search name, address, phone..."
                    className="w-full rounded-md border bg-background py-1.5 pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
            </div>
            {matches && matches.length > 0 && (
                <ul className="mt-1.5 max-h-32 overflow-y-auto rounded-md border bg-background text-sm">
                    {matches.map((c) => (
                        <li key={c.id} className="flex items-center">
                            <button
                                type="button"
                                onClick={() => onSelectCustomer(c.id)}
                                className="min-w-0 flex-1 text-left px-2 py-1.5 hover:bg-secondary"
                            >
                                <div className="font-medium leading-tight truncate">{c.company_name}</div>
                                {c.subtitle && (
                                    <div className="text-[10px] text-foreground/80 truncate">{c.subtitle}</div>
                                )}
                            </button>
                            {onLogCall && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onLogCall(c.id, c.company_name);
                                    }}
                                    className="shrink-0 flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium text-primary hover:bg-secondary"
                                    title="Log a call for this customer"
                                >
                                    <Phone className="h-3 w-3" />
                                    Log
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function RingingActions({ onAccept, onDecline }: { onAccept: () => void; onDecline: () => void }) {
    return (
        <div className="grid grid-cols-2 gap-2">
            <button
                type="button"
                onClick={onAccept}
                className="flex items-center justify-center gap-2 rounded-md bg-emerald-700 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 transition-colors"
            >
                <Phone className="h-4 w-4" />
                Answer
            </button>
            <button
                type="button"
                onClick={onDecline}
                className="flex items-center justify-center gap-2 rounded-md bg-destructive px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-opacity"
            >
                <PhoneOff className="h-4 w-4" />
                Voicemail
            </button>
        </div>
    );
}

/**
 * Live transcript (Phase 1: caller subtitles). The media bridge streams the
 * caller's leg to Deepgram and pushes each finalized utterance as a
 * `call:transcript` line; we scroll them in as the call runs. Two-tone bubbles
 * already distinguish caller vs office so Phase 2 (streaming the office leg
 * too) drops straight in. `live` shows the recording pulse + "Listening…".
 */
function LiveTranscript({ lines, live }: { lines: TranscriptLine[]; live: boolean }) {
    const bodyRef = useRef<HTMLDivElement>(null);
    // Pin to the newest line as it arrives.
    useEffect(() => {
        const el = bodyRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [lines.length]);

    return (
        <div className="overflow-hidden rounded-xl border bg-muted/30">
            <div className="flex items-center justify-between border-b bg-card px-3 py-2">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-foreground/70">
                    <span className="relative flex h-2 w-2">
                        {live && (
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500/60" />
                        )}
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                    </span>
                    Live transcript
                </div>
                <span className="text-[10px] font-medium text-muted-foreground">CC · caller</span>
            </div>
            <div ref={bodyRef} className="flex max-h-48 flex-col gap-2 overflow-y-auto p-3">
                {lines.map((l) => (
                    <div
                        key={l.seq}
                        className={cn(
                            "flex max-w-[88%] flex-col gap-0.5",
                            l.speaker === "office" ? "items-end self-end" : "items-start self-start",
                        )}
                    >
                        <span
                            className={cn(
                                "px-1 text-[9px] font-bold uppercase tracking-wide",
                                l.speaker === "office" ? "text-emerald-600" : "text-primary",
                            )}
                        >
                            {l.speaker === "office" ? "You" : "Caller"}
                        </span>
                        <span
                            className={cn(
                                "rounded-xl border px-2.5 py-1.5 text-[13px] leading-snug",
                                l.speaker === "office"
                                    ? "rounded-tr-sm border-emerald-500/20 bg-emerald-500/10 text-foreground"
                                    : "rounded-tl-sm border-border bg-secondary text-foreground",
                            )}
                        >
                            {l.text}
                        </span>
                    </div>
                ))}
            </div>
            {live && (
                <div className="flex items-center gap-2 border-t bg-card px-3 py-1.5 text-[11px] text-muted-foreground">
                    <span className="flex gap-0.5">
                        <span
                            className="h-1 w-1 animate-bounce rounded-full bg-foreground/40"
                            style={{ animationDelay: "0ms" }}
                        />
                        <span
                            className="h-1 w-1 animate-bounce rounded-full bg-foreground/40"
                            style={{ animationDelay: "150ms" }}
                        />
                        <span
                            className="h-1 w-1 animate-bounce rounded-full bg-foreground/40"
                            style={{ animationDelay: "300ms" }}
                        />
                    </span>
                    Listening…
                </div>
            )}
        </div>
    );
}

interface RosterMember {
    userId: string;
    name: string;
    role: string;
}

/** One vertical icon-over-label control button in the in-call control row. */
function CtrlButton({
    icon: Icon,
    label,
    active,
    activeTone = "primary",
    disabled,
    onClick,
}: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    active?: boolean;
    activeTone?: "amber" | "sky" | "primary";
    disabled?: boolean;
    onClick: () => void;
}) {
    const toneCls = active
        ? activeTone === "amber"
            ? "border-amber-500 bg-amber-500/10 text-amber-600"
            : activeTone === "sky"
              ? "border-sky-500 bg-sky-500/10 text-sky-600"
              : "border-primary bg-primary/10 text-primary"
        : "border-border bg-background text-foreground/80 hover:bg-muted";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={active}
            className={cn(
                "flex flex-col items-center justify-center gap-1 rounded-lg border px-1 py-2 text-[11px] font-medium leading-none transition-colors disabled:opacity-50",
                toneCls,
            )}
        >
            <Icon className="h-5 w-5" />
            {label}
        </button>
    );
}

/**
 * In-call control row: Mute + Keypad always; Hold + warm Hand-off added when the
 * server is in conference mode (and we have the caller's CallSid). Warm hand-off
 * dials a clocked-in office/manager/admin person (never techs — the server
 * roster excludes them) into the same call; you talk to them, then End Call to
 * drop off and leave them with the caller. The keypad (DTMF) and the hand-off
 * roster both expand below the row.
 */
function CallControls({
    muted,
    onToggleMute,
    onSendDigit,
    conference,
    callSid,
}: {
    muted: boolean;
    onToggleMute: () => void;
    onSendDigit: (digit: string) => void;
    conference: boolean;
    callSid?: string;
}) {
    const [showKeypad, setShowKeypad] = useState(false);
    const [held, setHeld] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const { data: rosterData, isFetching } = useQuery({
        queryKey: ["voice-roster"],
        queryFn: () => api.get<{ roster: RosterMember[] }>("/api/voice/roster"),
        enabled: conference && pickerOpen,
        refetchOnWindowFocus: false,
    });
    const roster = rosterData?.roster ?? [];
    const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

    const toggleHold = async () => {
        if (!callSid) return;
        const next = !held;
        setBusy(true);
        try {
            await api.post("/api/voice/hold", { callSid, hold: next });
            setHeld(next);
        } catch {
            toast.error("Couldn't change hold");
        } finally {
            setBusy(false);
        }
    };

    const handoff = async (m: RosterMember) => {
        if (!callSid) return;
        setBusy(true);
        try {
            await api.post("/api/voice/transfer", { callSid, toUserId: m.userId });
            toast.success(`Handing off to ${m.name}`, { description: "Talk to them, then hit End Call to complete." });
            setPickerOpen(false);
        } catch {
            toast.error("Hand-off failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-2">
            <div className={cn("grid gap-2", conference ? "grid-cols-4" : "grid-cols-2")}>
                <CtrlButton
                    icon={muted ? MicOff : Mic}
                    label={muted ? "Muted" : "Mute"}
                    active={muted}
                    activeTone="amber"
                    onClick={onToggleMute}
                />
                <CtrlButton
                    icon={Grid3x3}
                    label="Keypad"
                    active={showKeypad}
                    activeTone="primary"
                    onClick={() => setShowKeypad((v) => !v)}
                />
                {conference && (
                    <>
                        <CtrlButton
                            icon={held ? Play : Pause}
                            label={held ? "Resume" : "Hold"}
                            active={held}
                            activeTone="sky"
                            disabled={busy}
                            onClick={toggleHold}
                        />
                        <CtrlButton
                            icon={PhoneForwarded}
                            label="Hand off"
                            active={pickerOpen}
                            activeTone="primary"
                            disabled={busy}
                            onClick={() => setPickerOpen((v) => !v)}
                        />
                    </>
                )}
            </div>
            {showKeypad && (
                <div className="grid grid-cols-3 gap-1.5">
                    {keys.map((k) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => onSendDigit(k)}
                            className="rounded-md border border-border bg-background py-2.5 text-base font-semibold hover:bg-muted transition-colors"
                        >
                            {k}
                        </button>
                    ))}
                </div>
            )}
            {conference && pickerOpen && (
                <div className="rounded-md border border-border bg-background p-1">
                    {isFetching && <div className="px-2 py-2 text-xs text-muted-foreground">Loading who's on…</div>}
                    {!isFetching && roster.length === 0 && (
                        <div className="px-2 py-2 text-xs text-muted-foreground">
                            No other office staff clocked in right now.
                        </div>
                    )}
                    {roster.map((m) => (
                        <button
                            key={m.userId}
                            type="button"
                            onClick={() => handoff(m)}
                            disabled={busy}
                            className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                        >
                            <span className="font-medium">{m.name}</span>
                            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{m.role}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function PanelShortcut({
    icon: Icon,
    label,
    onClick,
    disabled,
    primary,
}: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    primary?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
                "flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs font-medium transition-colors",
                primary
                    ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                    : "bg-background hover:bg-secondary",
                disabled && "opacity-40 cursor-not-allowed",
            )}
        >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{label}</span>
        </button>
    );
}

/* ─── New Customer Intake (atomic customer + site + contact) ──────── */

interface NewCustomerIntakeProps {
    defaultPhone?: string;
    defaultName?: string;
    /** AI copilot draft to prefill the form (office reviews before submitting). */
    draft?: CallAiDraft;
    onBack: () => void;
    /** Customer is ready (created or chosen via dedupe) — advance to job step. */
    onReady: (customerId: string) => void;
}

function NewCustomerIntake({ defaultPhone, defaultName, draft, onBack, onReady }: NewCustomerIntakeProps) {
    const [businessName, setBusinessName] = useState(draft?.customerName ?? "");
    const [firstName, setFirstName] = useState(defaultName ?? "");
    const [phone, setPhone] = useState(defaultPhone ?? draft?.phone ?? "");
    const [addressLine, setAddressLine] = useState(draft?.addressLine ?? "");
    const [city, setCity] = useState(draft?.city ?? "");
    const [region, setRegion] = useState(draft?.region ?? "");
    const [postalCode, setPostalCode] = useState(draft?.postalCode ?? "");
    const [submitting, setSubmitting] = useState(false);
    const [conflicts, setConflicts] = useState<IntakeConflict[] | null>(null);

    const canSubmit =
        businessName.trim() &&
        firstName.trim() &&
        phone.trim() &&
        addressLine.trim() &&
        city.trim() &&
        region.trim() &&
        postalCode.trim();

    const submit = async () => {
        setSubmitting(true);
        setConflicts(null);
        try {
            const resp = await api.post<{ customer_id: string; site_id: string; contact_id: string }>(
                "/api/customers/intake",
                {
                    companyName: businessName.trim(),
                    source: "phone",
                    contact: { firstName: firstName.trim(), phone: phone.trim() },
                    site: {
                        addressLine: addressLine.trim(),
                        city: city.trim(),
                        region: region.trim(),
                        postalCode: postalCode.trim(),
                    },
                },
            );
            toast.success("Customer created");
            onReady(resp.customer_id);
        } catch (e) {
            if (e instanceof ApiError && e.status === 409) {
                const body = e.body as { conflicts?: IntakeConflict[] } | null;
                setConflicts(body?.conflicts ?? []);
            } else {
                toast.error("Could not create the customer. Check the fields and try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-3">
            <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-1 text-xs text-foreground/80 hover:text-foreground transition-colors"
            >
                <ArrowLeft className="h-3 w-3" />
                Back
            </button>

            <div className="text-sm font-semibold">New Customer</div>

            {conflicts && conflicts.length > 0 && (
                <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Possible duplicate
                    </div>
                    {conflicts.map((c) => (
                        <div key={c.conflict_customer_id} className="rounded-md border bg-background p-2 text-xs">
                            <div className="font-medium">{c.conflict_company_name}</div>
                            {c.detail && <div className="text-foreground/80">{c.detail}</div>}
                            <Button
                                size="sm"
                                variant="outline"
                                className="mt-1.5 w-full"
                                onClick={() => onReady(c.conflict_customer_id)}
                            >
                                Use existing customer
                            </Button>
                        </div>
                    ))}
                    <p className="text-[11px] text-foreground/80">
                        Or adjust the details below and submit again to create a new record.
                    </p>
                </div>
            )}

            <div className="space-y-2">
                <Field label="Business name" value={businessName} onChange={setBusinessName} placeholder="Acme LLC" />
                <Field label="Contact first name" value={firstName} onChange={setFirstName} placeholder="Jane" />
                <Field label="Contact phone" value={phone} onChange={setPhone} placeholder="(512) 555-0199" />
                <Field
                    label="Service address"
                    value={addressLine}
                    onChange={setAddressLine}
                    placeholder="123 Main St"
                />
                <div className="grid grid-cols-3 gap-2">
                    <Field label="City" value={city} onChange={setCity} placeholder="Dallas" />
                    <Field label="State" value={region} onChange={setRegion} placeholder="TX" />
                    <Field label="ZIP" value={postalCode} onChange={setPostalCode} placeholder="75001" />
                </div>
            </div>

            <Button className="w-full" disabled={!canSubmit || submitting} onClick={submit}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                Create Customer
            </Button>
        </div>
    );
}

function Field({
    label,
    value,
    onChange,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
}) {
    return (
        <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">{label}</label>
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="mt-1"
            />
        </div>
    );
}

/* ─── Wrap-Up View ────────────────────────────────────────────────── */

interface WrapUpProps {
    call: IncomingCall;
    customerSummary: CustomerSummary | undefined;
    onSaveNotes: () => Promise<void>;
    onClose: () => void;
}

function WrapUpView({ call, customerSummary, onSaveNotes, onClose }: WrapUpProps) {
    const [saving, setSaving] = useState(false);
    const duration =
        call.callStartedAt && call.callEndedAt ? Math.round((call.callEndedAt - call.callStartedAt) / 1000) : null;
    const mins = duration ? Math.floor(duration / 60) : 0;
    const secs = duration ? duration % 60 : 0;

    return (
        <div className="space-y-4">
            <div className="rounded-lg border bg-secondary/30 p-3 space-y-2">
                <h3 className="text-sm font-semibold">Call Summary</h3>
                {duration != null && (
                    <div className="flex items-center gap-1.5 text-xs text-foreground/80">
                        <Clock className="h-3.5 w-3.5" />
                        Duration: {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
                    </div>
                )}
                {customerSummary && (
                    <div className="text-xs">
                        Customer: <span className="font-semibold">{customerSummary.company_name}</span>
                    </div>
                )}
                {call.notes && (
                    <div className="text-xs">
                        <span className="font-semibold">Notes:</span>
                        <p className="mt-0.5 text-foreground/80 whitespace-pre-wrap">{call.notes}</p>
                    </div>
                )}
                {call.createdJobId && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Job created
                    </div>
                )}
            </div>

            <div className="grid gap-2">
                <Button
                    variant="outline"
                    onClick={async () => {
                        setSaving(true);
                        await onSaveNotes();
                        setSaving(false);
                    }}
                    disabled={saving}
                    className="w-full"
                >
                    {saving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <StickyNote className="mr-2 h-4 w-4" />
                    )}
                    Save Call Log
                </Button>
                <Button variant="ghost" onClick={onClose} className="w-full">
                    Close
                </Button>
            </div>

            <div className="text-[10px] text-center text-foreground/80">Auto-closing in 30 seconds...</div>
        </div>
    );
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

function hasDraftContent(d?: CallAiDraft): boolean {
    return !!d && Object.values(d).some((v) => v != null && v !== "");
}

/** Live AI copilot draft shown during an active call — review, then act on it. */
function AiDraftCard({ draft, onUse }: { draft: CallAiDraft; onUse?: () => void }) {
    const address = [draft.addressLine, draft.city, draft.region, draft.postalCode].filter(Boolean).join(", ");
    const rows: Array<[string, string | null]> = [
        ["Service", draft.serviceType],
        ["Address", address || null],
        ["Caller", draft.customerName],
        ["Urgency", draft.urgency],
    ];
    return (
        <div className="space-y-2 rounded-xl border border-primary/30 bg-gradient-to-b from-primary/5 to-primary/10 p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="h-3.5 w-3.5" /> AI Copilot — live draft
            </div>
            {draft.summary && <p className="text-xs text-foreground/90">{draft.summary}</p>}
            <div className="grid gap-0.5 text-xs">
                {rows.map(([label, val]) =>
                    val ? (
                        <div key={label} className="flex gap-1.5">
                            <span className="w-16 shrink-0 text-foreground/60">{label}</span>
                            <span className="font-medium">{val}</span>
                        </div>
                    ) : null,
                )}
            </div>
            {onUse && (
                <Button size="sm" variant="outline" className="mt-1 w-full" onClick={onUse}>
                    <UserPlus className="mr-2 h-3.5 w-3.5" /> New customer from call
                </Button>
            )}
        </div>
    );
}

function digitsOnly(s: string): string {
    return s.replace(/\D/g, "");
}

function formatShortDate(raw: string | null): string {
    if (!raw) return "—";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
}

function formatPhoneNumber(raw: string): string {
    const digits = digitsOnly(raw);
    if (digits.length === 11 && digits.startsWith("1")) {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return raw;
}
